import { useCallback, useEffect } from 'react';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useGoogleAuth } from './GoogleAuthContext';
import {
  type YouTubeVideo,
  type YouTubeSearchParams,
  type YouTubeSearchResponse,
  type YouTubeApiVideoItem,
  type YouTubeApiPlaylistItem,
  type YouTubeApiChannelItem,
  type YouTubeApiResponse,
  type YouTubeApiRequestOptions,
  GoogleService,
} from './types';

export const useYouTubeService = () => {
  const { isLoading: authLoading, authorize: googleAuthorize, authError, setAuthError, logout, getServiceToken, isAuthenticated } = useGoogleAuth();

  const { showToastNotification } = useToastNotificationContext();

  // 1. Derive token status directly during render pass (replaces the old useState)
  const youtubeToken = getServiceToken(GoogleService.YouTube);
  const isGapiTokenSet = Boolean(isAuthenticated(GoogleService.YouTube) && youtubeToken && window.gapi?.client);

  // 2. Synchronize the external GAPI client token state safely
  useEffect(() => {
    if (isGapiTokenSet && youtubeToken) {
      window.gapi.client.setToken({
        access_token: youtubeToken.access_token,
      });
    }
  }, [isGapiTokenSet, youtubeToken]);

  // 3. Handle authError notifications cleanly without triggering cascading loops
  useEffect(() => {
    if (!authError) return;

    showToastNotification({
      notificationText: authError,
      type: NotificationTypeEnum.Error,
      showCloseButton: true,
    });

    setAuthError(undefined);
  }, [authError, showToastNotification, setAuthError]);

  // 4. Verify runtime readiness
  const ensureReady = useCallback((): void => {
    if (!isAuthenticated(GoogleService.YouTube) || !isGapiTokenSet) {
      throw new Error('YouTube service not ready - user not authenticated or token not set');
    }
  }, [isAuthenticated, isGapiTokenSet]);

  const authorizeYouTube = useCallback(async (): Promise<void> => {
    await googleAuthorize(GoogleService.YouTube);
  }, [googleAuthorize]);

  const getVideoId = useCallback((item: YouTubeApiPlaylistItem): string => {
    if (item.snippet?.resourceId?.videoId) {
      return item.snippet.resourceId.videoId;
    }
    if (typeof item.id === 'object' && item.id?.videoId) {
      return item.id.videoId;
    }
    if (typeof item.id === 'string') {
      return item.id;
    }

    return '';
  }, []);

  const parseDuration = useCallback((duration: string): number => {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);

    if (!match) return 0;

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);

    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }, []);

  const getVideoMetadata = useCallback(
    async (videoIds: string[]): Promise<YouTubeVideo[]> => {
      ensureReady();

      try {
        const response: YouTubeApiResponse<YouTubeApiVideoItem> = await window.gapi.client.youtube.videos.list({
          id: videoIds,
          part: ['id', 'snippet', 'statistics', 'contentDetails'],
        });

        return (response.result?.items || []).map((item: YouTubeApiVideoItem) => ({
          id: item.id,
          title: item.snippet?.title || 'Untitled',
          description: item.snippet?.description || '',
          thumbnailUrl: item.snippet?.thumbnails?.medium?.url || item.snippet?.thumbnails?.default?.url || '',
          duration: parseDuration(item.contentDetails?.duration || 'PT0S'),
          viewCount: item.statistics?.viewCount || '0',
          publishedAt: item.snippet?.publishedAt || '',
          channelTitle: item.snippet?.channelTitle || '',
        }));
      } catch {
        throw new Error('failed to get video metadata for youtube');
      }
    },
    [ensureReady, parseDuration]
  );

  const getUserChannel = useCallback(async (): Promise<{ playlistId?: string; channelId?: string }> => {
    ensureReady();

    return new Promise((resolve, reject) => {
      window.gapi.client.youtube.channels
        .list({
          mine: true,
          part: ['id', 'contentDetails'],
        })
        .then((response: YouTubeApiResponse<YouTubeApiChannelItem>) => {
          if (response.result?.items && response.result.items.length > 0) {
            resolve({
              playlistId: response.result.items[0].contentDetails?.relatedPlaylists?.uploads,
              channelId: response.result.items[0].id,
            });
          } else {
            resolve({});
          }
        })
        .catch(reject);
    });
  }, [ensureReady]);

  const getPlaylistVideos = useCallback(
    async (playlistId: string, pageToken?: string): Promise<YouTubeSearchResponse> => {
      ensureReady();

      const requestOptions: YouTubeApiRequestOptions = {
        playlistId,
        part: ['snippet', 'id'],
        maxResults: 10,
      };

      if (pageToken) {
        requestOptions.pageToken = pageToken;
      }

      return new Promise((resolve, reject) => {
        window.gapi.client.youtube.playlistItems
          .list(requestOptions)
          .then(async (response: YouTubeApiResponse<YouTubeApiPlaylistItem>) => {
            if (!response.result?.items) {
              resolve({
                videos: [],
                totalResults: 0,
              });

              return;
            }

            const videoIds = response.result.items.map((item: YouTubeApiPlaylistItem) => getVideoId(item));
            const videosWithMetadata = await getVideoMetadata(videoIds);

            resolve({
              videos: videosWithMetadata,
              nextPageToken: response.result.nextPageToken,
              prevPageToken: response.result.prevPageToken,
              totalResults: response.result.pageInfo?.totalResults || 0,
            });
          })
          .catch(reject);
      });
    },
    [ensureReady, getVideoId, getVideoMetadata]
  );

  const searchVideos = useCallback(
    async (params: YouTubeSearchParams = {}): Promise<YouTubeSearchResponse> => {
      ensureReady();

      const requestOptions: YouTubeApiRequestOptions = {
        part: ['snippet', 'id'],
        maxResults: params.maxResults || 10,
        type: 'video',
      };

      if (params.query) {
        requestOptions.q = params.query;
      } else {
        const channelResponse = await getUserChannel();

        if (channelResponse.playlistId) {
          return getPlaylistVideos(channelResponse.playlistId, params.pageToken);
        }
      }

      if (params.pageToken) {
        requestOptions.pageToken = params.pageToken;
      }

      return new Promise((resolve, reject) => {
        window.gapi.client.youtube.search
          .list(requestOptions)
          .then(async (response: YouTubeApiResponse<YouTubeApiPlaylistItem>) => {
            if (!response.result?.items) {
              resolve({
                videos: [],
                totalResults: 0,
              });

              return;
            }

            const videoIds = response.result.items.map((item: YouTubeApiPlaylistItem) => getVideoId(item));
            const videosWithMetadata = await getVideoMetadata(videoIds);

            resolve({
              videos: videosWithMetadata,
              nextPageToken: response.result.nextPageToken,
              prevPageToken: response.result.prevPageToken,
              totalResults: response.result.pageInfo?.totalResults || 0,
            });
          })
          .catch(reject);
      });
    },
    [ensureReady, getUserChannel, getPlaylistVideos, getVideoId, getVideoMetadata]
  );

  return {
    isAuthenticated: isAuthenticated(GoogleService.YouTube),
    isLoading: authLoading,
    authorizeYouTube,
    logout,
    searchVideos,
  };
};
